import React, { useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import { Check, ExternalLink, Slash, Square, Tool } from "react-feather";
import { Link } from "react-router";
import {
  IWorkPipelineItem,
  IWorkPipelineStopTemplateRunBody,
  IWorkPipelineTemplateRun,
  TEMPLATE_RUN_RULE,
  WORK_PIPELINE_SITE_REFRESH_REASON,
  WorkPipelineStage,
} from "../../interfaces";
import { since } from "./template-help-meta";

const OUTCOME_BADGE: Record<IWorkPipelineTemplateRun["outcome"], { bg: string; label: string }> = {
  running: { bg: "info", label: "Running" },
  passed: { bg: "success", label: "Passed" },
  failed: { bg: "danger", label: "Failed" },
  stopped: { bg: "secondary", label: "Stopped" },
};

const RECENT_RUNS = 5;

/** In-scope tester failures, counted the way the server's one-run gate counts them. */
export const testerRunFailed = (item: IWorkPipelineItem): boolean => {
  const from = item.templateRunsSince ? new Date(item.templateRunsSince).getTime() : 0;
  return (
    (item.templateRuns ?? []).filter(
      (r) => r.outcome === "failed" && !r.byLead && new Date(r.startedAt).getTime() >= from
    ).length >= TEMPLATE_RUN_RULE.testerFailuresAllowed
  );
};

/** The latest run on a board card: who ran it and how it ended. */
export const TemplateRunBadge: React.FC<{ item: IWorkPipelineItem }> = ({ item }) => {
  const last = (item.templateRuns ?? [])[item.templateRuns.length - 1];
  if (!last) return null;
  const o = OUTCOME_BADGE[last.outcome];
  return (
    <Badge
      bg={o.bg}
      title={`Extension template run by ${last.byName ?? "a tester"}${last.byLead ? " (lead)" : ""}, ${since(last.startedAt)}${last.note ? `: ${last.note}` : ""}`}
    >
      <Tool size={10} className="me-1" />
      Template run {o.label.toLowerCase()} · {last.byName ?? "tester"}
      {last.syncedAt ? " · live" : ""}
    </Badge>
  );
};

/** Template-creation runs from the extension for this row, and what the tester does next. */
export const TemplateRunsPanel: React.FC<{
  item: IWorkPipelineItem;
  claimedByMe: boolean;
  canComplete: boolean;
  busy: boolean;
  onComplete: () => void;
  onProposeBlock: () => void;
  onStopRun: (correlationId: string, body: IWorkPipelineStopTemplateRunBody) => Promise<boolean>;
}> = ({ item, claimedByMe, canComplete, busy, onComplete, onProposeBlock, onStopRun }) => {
  const [stopping, setStopping] = useState<IWorkPipelineTemplateRun | null>(null);
  const [stopNote, setStopNote] = useState("");
  const [siteRefresh, setSiteRefresh] = useState(true);
  const [stopBusy, setStopBusy] = useState(false);
  const runs = [...(item.templateRuns ?? [])].reverse();
  const building = item.stage === WorkPipelineStage.TemplateBuilding;
  if (!building && !runs.length) return null;
  const last = runs[0];
  const blocked = testerRunFailed(item);

  const next = !last ? (
    <span className="text-muted">
      No template runs yet. Build the template with the Checkout Extension while you hold the
      claim; the run reports back here.
    </span>
  ) : last.outcome === "running" ? (
    <span className="d-flex align-items-center gap-2 flex-wrap">
      <span>
        {last.byName ?? "A tester"} started a run {since(last.startedAt)}. Its Pass or Fail
        verdict lands here. If the site refreshed or the tab closed, no verdict will come: stop
        the run with a note.
      </span>
      {claimedByMe && (
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={busy}
          title="End this run without a verdict and say why; a stopped run doesn't count as a failure"
          onClick={() => {
            setStopping(last);
            setStopNote("");
            setSiteRefresh(true);
          }}
        >
          <Square size={13} className="me-1" /> Stop run
        </Button>
      )}
    </span>
  ) : last.outcome === "stopped" ? (
    <span>
      The run was stopped{last.note ? `: ${last.note}` : ""}. Run it again in the extension, or
      propose a block if the site can't be built.
    </span>
  ) : last.outcome === "passed" && last.syncedAt ? (
    <span className="d-flex align-items-center gap-2 flex-wrap">
      <span>The template passed and is live in SQL. Mark this stage complete.</span>
      {claimedByMe && building && (
        <Button
          size="sm"
          variant="success"
          disabled={busy || !canComplete}
          title={
            canComplete
              ? "Finish Template building; the row moves to its next bucket"
              : "Resolve the blockers below first"
          }
          onClick={onComplete}
        >
          <Check size={13} className="me-1" /> Mark complete
        </Button>
      )}
    </span>
  ) : last.outcome === "passed" ? (
    <span>
      The template passed. Push it live from{" "}
      <Link
        to="/auto-coupons/templates"
        target="_blank"
        title={`Find ${item.domain} and push its passing template to SQL so it goes live`}
      >
        Templates <ExternalLink size={11} />
      </Link>
      , then mark this stage complete.
    </span>
  ) : (
    <span className="d-flex align-items-center gap-2 flex-wrap">
      <span>
        The run failed{last.note ? `: ${last.note}` : ""}.{" "}
        {blocked && !last.byLead ? `${TEMPLATE_RUN_RULE.blockedMessage} ` : ""}
        If the site can't be tested, propose a block or set a brand status (Not ecomm, Access
        denied); otherwise add a note with Need assistance.
      </span>
      {claimedByMe && (
        <Button
          size="sm"
          variant="outline-warning"
          disabled={busy}
          title="Ask a manager to archive it with a reason and a note"
          onClick={onProposeBlock}
        >
          <Slash size={13} className="me-1" /> Propose block
        </Button>
      )}
    </span>
  );

  return (
    <Alert
      variant={last?.outcome === "failed" ? "warning" : last?.outcome === "passed" ? "success" : "light"}
      className="border py-2 small"
    >
      <div className="d-flex align-items-center gap-2 mb-1">
        <Tool size={14} />
        <strong>Template runs</strong>
        {blocked && (
          <Badge bg="danger" title="A tester's run failed this cycle; only a lead can run it again">
            {TEMPLATE_RUN_RULE.blockedMessage}
          </Badge>
        )}
      </div>
      <div className="mb-2">{next}</div>
      {runs.slice(0, RECENT_RUNS).map((r) => (
        <div key={r.correlationId} className="border-top pt-1 mt-1 d-flex flex-wrap gap-2 align-items-center">
          <Badge bg={OUTCOME_BADGE[r.outcome].bg}>{OUTCOME_BADGE[r.outcome].label}</Badge>
          <span>
            {r.byName ?? "Tester"}
            {r.byLead ? " (lead)" : ""}
          </span>
          {r.builds?.length > 0 && (
            <span className="text-muted" title="What the run set out to build; it passes only if each passes">
              {r.builds.map((b) => (b === "sku" ? "SKU" : "auto")).join(" + ")}
            </span>
          )}
          <span className="text-muted">{since(r.startedAt)}</span>
          {r.syncedAt && <Badge bg="success" title={`Synced ${new Date(r.syncedAt).toLocaleString()}`}>Live</Badge>}
          {r.note && <span className="text-muted text-truncate" style={{ maxWidth: 360 }} title={r.note}>{r.note}</span>}
          <Link
            to={`/auto-coupons/runs?correlationId=${encodeURIComponent(r.correlationId)}`}
            target="_blank"
            className="ms-auto"
            title="Open the run's logs"
          >
            Logs <ExternalLink size={11} />
          </Link>
        </div>
      ))}
      <Modal show={!!stopping} onHide={() => setStopping(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Stop template run</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Ends {stopping?.byName ?? "the"} run on <strong>{item.domain}</strong> without a verdict.
            It doesn't count towards the one-run rule.
          </p>
          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={500}
              placeholder="What happened? e.g. the page reloaded at checkout and the run stopped"
              value={stopNote}
              onChange={(e) => setStopNote(e.target.value)}
            />
          </Form.Group>
          <Form.Check
            id="stop-run-site-refresh"
            checked={siteRefresh}
            onChange={(e) => setSiteRefresh(e.target.checked)}
            label={WORK_PIPELINE_SITE_REFRESH_REASON.label}
          />
          <Form.Text>
            Proposes a "{WORK_PIPELINE_SITE_REFRESH_REASON.label}" block and releases your claim.
            The extension won't start another run here while it's open; a manager approves it into
            Archived or sends it back.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setStopping(null)}>
            Cancel
          </Button>
          <Button
            variant={siteRefresh ? "warning" : "primary"}
            disabled={stopBusy || (siteRefresh && !stopNote.trim())}
            title={siteRefresh && !stopNote.trim() ? "Add a note for the manager reviewing the block" : undefined}
            onClick={async () => {
              if (!stopping) return;
              setStopBusy(true);
              const ok = await onStopRun(stopping.correlationId, { note: stopNote, siteRefresh });
              setStopBusy(false);
              if (ok) setStopping(null);
            }}
          >
            {stopBusy && <Spinner animation="border" size="sm" className="me-1" />}
            {siteRefresh ? "Stop run & propose block" : "Stop run"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Alert>
  );
};
